/**
 * Conversation Store
 * SpecReferenceID: GALEN-CHAT-V1
 * Requirements: GALEN-CHAT-V1-R08, GALEN-CHAT-V1-R09, AUTH-V1-R15
 *
 * Zustand store for persistent conversation history backed by the API.
 * Conversations are fetched from / persisted to the backend users table.
 * Falls back to localStorage when the user is not authenticated.
 *
 * GALEN-CHAT-V1-R08: Maintain conversation history until user resets or session expires.
 * GALEN-CHAT-V1-R09: Enable follow-up questions with conversation context.
 * AUTH-V1-R15: Per-user state persistence via the backend conversations API.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import api from '@/lib/api';
import type { ChatMessage } from '@/types';

const MAX_CONVERSATIONS = 50;

/** A single conversation session. */
export interface Conversation {
  id: string;
  title: string;
  messages: ChatMessage[];
  /** Backend conversation ID for follow-up context. */
  conversationId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ConversationState {
  conversations: Conversation[];
  activeConversationId: string | null;
  /** Whether conversations have been loaded from the API for the current session. */
  _synced: boolean;

  syncFromApi: () => Promise<void>;
  createConversation: (firstMessage: ChatMessage) => string;
  setActiveConversation: (id: string | null) => void;
  addMessage: (conversationId: string, message: ChatMessage) => void;
  updateMessage: (conversationId: string, messageId: string, updates: Partial<ChatMessage>) => void;
  setBackendConversationId: (conversationId: string, backendId: string) => void;
  deleteConversation: (id: string) => void;
  clearAllConversations: () => void;
}

/** Threshold below which the title is considered too short and may be improved. */
const SHORT_TITLE_THRESHOLD = 8;

/** Generate a title from message content, truncated to 60 chars. */
function generateTitle(content: string): string {
  const trimmed = content.trim().replace(/\n/g, ' ');
  if (trimmed.length <= 60) return trimmed;
  return trimmed.slice(0, 57) + '...';
}

/** Prune oldest conversations if we exceed the cap. */
function pruneConversations(conversations: Conversation[]): Conversation[] {
  if (conversations.length <= MAX_CONVERSATIONS) return conversations;
  const sorted = [...conversations].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
  return sorted.slice(0, MAX_CONVERSATIONS);
}

/** API message shape from backend. */
interface ApiMessage {
  id: string;
  role: string;
  content: string;
  citations?: ChatMessage['citations'];
  is_grounded?: boolean;
  created_at?: string;
}

/** API conversation shape from backend. */
interface ApiConversation {
  id: string;
  title: string;
  messages?: ApiMessage[];
  created_at: string;
  updated_at: string;
}

/** Convert API conversation response to local shape. */
function apiToLocal(apiConv: ApiConversation): Conversation {
  const messages = apiConv.messages ?? [];
  return {
    id: apiConv.id,
    title: apiConv.title || 'New Conversation',
    messages: messages.map((m) => ({
      id: m.id,
      role: m.role as 'user' | 'assistant',
      content: m.content,
      citations: m.citations,
      is_grounded: m.is_grounded,
      timestamp: m.created_at ?? new Date().toISOString(),
    })),
    conversationId: apiConv.id,
    createdAt: apiConv.created_at,
    updatedAt: apiConv.updated_at,
  };
}

export const useConversationStore = create<ConversationState>()(
  persist(
    (set, get) => ({
      conversations: [],
      activeConversationId: null,
      _synced: false,

      syncFromApi: async () => {
        try {
          const token = localStorage.getItem('auth-token');
          if (!token) return; // not authenticated — keep local store

          const { data } = await api.get<{ conversations: ApiConversation[] }>(
            '/conversations',
            { params: { page: 1, page_size: MAX_CONVERSATIONS } },
          );

          const conversations: Conversation[] = [];
          for (const c of data.conversations) {
            // Fetch full conversation with messages
            const detail = await api.get<ApiConversation>(`/conversations/${c.id}`);
            conversations.push(apiToLocal(detail.data));
          }

          set({ conversations, _synced: true });
        } catch {
          // Silently fall back to local store
        }
      },

      createConversation: (firstMessage: ChatMessage) => {
        const now = new Date().toISOString();
        const id = `conv-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        const conversation: Conversation = {
          id,
          title: generateTitle(firstMessage.content),
          messages: [firstMessage],
          conversationId: null,
          createdAt: now,
          updatedAt: now,
        };

        set((state) => ({
          conversations: pruneConversations([conversation, ...state.conversations]),
          activeConversationId: id,
        }));

        // Bug #4332: The chat streaming endpoint is the authoritative writer
        // for chat-flow conversations and messages — it creates the backend
        // conversation row on first turn and commits both user and assistant
        // messages. This store used to also POST /v1/conversations and
        // /v1/conversations/{id}/messages here, but that duplicated every
        // row (once from this store, once from the chat endpoint) now that
        // the chat endpoint's persistence actually lands. The backend
        // conversation UUID arrives via the SSE done event and is captured
        // via setBackendConversationId — no preflight round-trip needed.
        return id;
      },

      setActiveConversation: (id: string | null) => {
        set({ activeConversationId: id });
      },

      addMessage: (conversationId: string, message: ChatMessage) => {
        const now = new Date().toISOString();
        set((state) => ({
          conversations: state.conversations.map((conv) => {
            if (conv.id !== conversationId) return conv;

            // Auto-upgrade short titles with the first assistant response snippet
            const shouldUpgradeTitle =
              message.role === 'assistant' &&
              conv.title.length < SHORT_TITLE_THRESHOLD &&
              !conv.messages.some((m) => m.role === 'assistant');

            return {
              ...conv,
              messages: [...conv.messages, message],
              updatedAt: now,
              ...(shouldUpgradeTitle
                ? { title: generateTitle(message.content) }
                : {}),
            };
          }),
        }));

        // Bug #4332: No backend round-trip. The chat streaming endpoint
        // persists user + assistant messages authoritatively; this store
        // is a local cache only. Posting here in addition to the chat
        // endpoint duplicated every row once the chat endpoint's
        // persistence started actually landing.
      },

      updateMessage: (conversationId: string, messageId: string, updates: Partial<ChatMessage>) => {
        set((state) => ({
          conversations: state.conversations.map((conv) =>
            conv.id === conversationId
              ? {
                  ...conv,
                  messages: conv.messages.map((msg) =>
                    msg.id === messageId ? { ...msg, ...updates } : msg
                  ),
                }
              : conv
          ),
        }));
      },

      setBackendConversationId: (conversationId: string, backendId: string) => {
        set((state) => ({
          conversations: state.conversations.map((conv) =>
            conv.id === conversationId
              ? { ...conv, conversationId: backendId }
              : conv
          ),
        }));
      },

      deleteConversation: (id: string) => {
        const state = get();
        const conv = state.conversations.find((c) => c.id === id);

        set({
          conversations: state.conversations.filter((c) => c.id !== id),
          activeConversationId:
            state.activeConversationId === id ? null : state.activeConversationId,
        });

        // Delete from backend
        const backendId = conv?.conversationId;
        if (backendId && localStorage.getItem('auth-token')) {
          api.delete(`/conversations/${backendId}`).catch(() => {});
        }
      },

      clearAllConversations: () => {
        set({
          conversations: [],
          activeConversationId: null,
        });

        // Clear from backend
        if (localStorage.getItem('auth-token')) {
          api.delete('/conversations').catch(() => {});
        }
      },
    }),
    {
      name: 'galen-conversations-storage',
      storage: createJSONStorage(() => {
        // Check for a fully functional localStorage (with setItem method).
        // Node.js v25+ exposes a non-standard globalThis.localStorage without
        // standard Web Storage methods — fall back to in-memory storage.
        if (typeof globalThis.localStorage?.setItem === 'function') {
          return globalThis.localStorage;
        }
        // In-memory fallback for environments without proper localStorage
        const store = new Map<string, string>();
        return {
          getItem: (key: string) => store.get(key) ?? null,
          setItem: (key: string, value: string) => { store.set(key, value); },
          removeItem: (key: string) => { store.delete(key); },
        };
      }),
      partialize: (state) => ({
        conversations: state.conversations,
        activeConversationId: state.activeConversationId,
      }),
    }
  )
);

// Selector hooks for cleaner component usage
export const useConversations = (): Conversation[] =>
  useConversationStore((state) => state.conversations);

export const useActiveConversationId = (): string | null =>
  useConversationStore((state) => state.activeConversationId);

export const useActiveConversation = (): Conversation | undefined =>
  useConversationStore((state) =>
    state.conversations.find((c) => c.id === state.activeConversationId)
  );
